import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import { AppDispatch } from "../store";
import { RootState } from "../reducers/hooks";
import { alertMessage } from "../actions/common";
import { errorHandler } from "app/shared/Service/errorHandler";
import { uploadImageToAzure } from "app/common/Filter/utils";
import {
  confirmCatalogImportAPI,
  importCatalogAPI,
} from "app/services/catalogs";
import {
  CatalogImportPortion,
  CatalogImportPreviewData,
  CatalogImportStep,
  CatalogImportUploadedFile,
  CatalogImportValidationError,
} from "app/types/catalog-import";
import { getCatalogMenu, getMenuById } from "../actions/catalogs";
import i18n from "app/config/i18n";

interface CatalogImportState {
  step: CatalogImportStep;
  isUploading: boolean;
  isSubmitting: boolean;
  previewData: CatalogImportPreviewData | null;
  validationErrors: CatalogImportValidationError[];
  warnings: string[];
  uploadedFile: CatalogImportUploadedFile | null;
  importLogId: number | null;
  catalogId: number | null;
  portions: CatalogImportPortion[];
}

const initialState: CatalogImportState = {
  step: "upload",
  isUploading: false,
  isSubmitting: false,
  previewData: null,
  validationErrors: [],
  warnings: [],
  uploadedFile: null,
  importLogId: null,
  catalogId: null,
  portions: [],
};

export const catalogImportSlice = createSlice({
  name: "catalogImport",
  initialState,
  reducers: {
    setImportStep: (state, action: PayloadAction<CatalogImportStep>) => {
      state.step = action.payload;
    },
    setIsUploading: (state, action: PayloadAction<boolean>) => {
      state.isUploading = action.payload;
    },
    setIsSubmitting: (state, action: PayloadAction<boolean>) => {
      state.isSubmitting = action.payload;
    },
    setPreviewData: (
      state,
      action: PayloadAction<CatalogImportPreviewData | null>
    ) => {
      state.previewData = action.payload;
    },
    setValidationErrors: (
      state,
      action: PayloadAction<CatalogImportValidationError[]>
    ) => {
      state.validationErrors = action.payload;
    },
    setWarnings: (state, action: PayloadAction<string[]>) => {
      state.warnings = action.payload;
    },
    setUploadedFile: (
      state,
      action: PayloadAction<CatalogImportUploadedFile | null>
    ) => {
      state.uploadedFile = action.payload;
    },
    setImportLogId: (state, action: PayloadAction<number | null>) => {
      state.importLogId = action.payload;
    },
    setImportCatalogId: (state, action: PayloadAction<number | null>) => {
      state.catalogId = action.payload;
    },
    setPortions: (state, action: PayloadAction<CatalogImportPortion[]>) => {
      state.portions = action.payload;
    },
    resetCatalogImport: () => initialState,
  },
});

export const {
  setImportStep,
  setIsUploading,
  setIsSubmitting,
  setPreviewData,
  setValidationErrors,
  setWarnings,
  setUploadedFile,
  setImportLogId,
  setImportCatalogId,
  setPortions,
  resetCatalogImport,
} = catalogImportSlice.actions;

export default catalogImportSlice.reducer;

type UploadAndPreviewImportPayload = {
  catalogId: number;
  file: File;
};

export const uploadAndPreviewImportAction =
  ({ catalogId, file }: UploadAndPreviewImportPayload) =>
  async (dispatch: AppDispatch) => {
    dispatch(setImportCatalogId(catalogId));
    dispatch(setIsUploading(true));

    try {
      const fileUrl = await uploadImageToAzure(file, "menu", "import");
      const uploadedAt = new Date().toISOString();

      dispatch(
        setUploadedFile({
          name: file.name,
          uploadedAt,
          url: fileUrl,
        })
      );

      const { data } = await importCatalogAPI(catalogId, { fileUrl });
      const previewData = data.data;

      dispatch(setPreviewData(previewData));
      dispatch(setPortions(data.included?.portions ?? []));
      dispatch(setImportLogId(previewData.importLogId));
      dispatch(setWarnings(previewData.warnings ?? []));
      dispatch(setValidationErrors([]));
      dispatch(setImportStep("preview"));
    } catch (error: unknown) {
      const axiosError = error as {
        response?: { status?: number; data?: { data?: { valid?: boolean; errors?: CatalogImportValidationError[]; importLogId?: number } } };
      };

      if (
        axiosError.response?.status === 422 &&
        axiosError.response.data?.data?.valid === false
      ) {
        const errorData = axiosError.response.data.data;
        dispatch(setValidationErrors(errorData.errors ?? []));
        dispatch(setImportLogId(errorData.importLogId ?? null));
        dispatch(setPreviewData(null));
        dispatch(setImportStep("errors"));
      } else {
        const message = errorHandler(error);
        dispatch(alertMessage(message, "error"));
      }
    } finally {
      dispatch(setIsUploading(false));
    }
  };

type ConfirmCatalogImportPayload = {
  catalogId: number;
  importLogId: number;
  onSuccess: () => void;
};

export const confirmCatalogImportAction =
  ({ catalogId, importLogId, onSuccess }: ConfirmCatalogImportPayload) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(setIsSubmitting(true));

    try {
      await confirmCatalogImportAPI(catalogId, { catalogId, importLogId });

      dispatch(alertMessage(i18n.t("pages.catalog.import.confirmSuccess"), "success"));

      await dispatch(getCatalogMenu(catalogId));

      const { activeCatalogMenu } = getState().catalogs;
      if (activeCatalogMenu?.id) {
        await dispatch(getMenuById(catalogId, activeCatalogMenu.id));
      }

      dispatch(resetCatalogImport());
      onSuccess();
    } catch (error) {
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    } finally {
      dispatch(setIsSubmitting(false));
    }
  };

export const resetCatalogImportAction = () => (dispatch: AppDispatch) => {
  dispatch(resetCatalogImport());
};

export const backToUploadStepAction = () => (dispatch: AppDispatch) => {
  dispatch(setImportStep("upload"));
  dispatch(setPreviewData(null));
  dispatch(setValidationErrors([]));
  dispatch(setWarnings([]));
  dispatch(setImportLogId(null));
  dispatch(setUploadedFile(null));
  dispatch(setPortions([]));
};
